import { createFileRoute, Outlet, Link, useLocation } from '@tanstack/react-router';
import { RoleGuard } from '@/components/layout/RoleGuard';
import { cn } from '@/lib/cn';
import { BarChart3, Receipt, GitCompareArrows } from 'lucide-react';

export const Route = createFileRoute('/_app/financials')({
  component: () => (
    <RoleGuard allowedRoles={['supervisor', 'owner', 'operator']}>
      <FinancialsLayout />
    </RoleGuard>
  ),
});

interface TabDef {
  name: string;
  href: string;
  icon: typeof BarChart3;
  exact?: boolean;
}

const tabs: TabDef[] = [
  { name: 'Overview', href: '/financials', icon: BarChart3, exact: true },
  { name: 'Expenses', href: '/financials/expenses', icon: Receipt },
  { name: 'Comparison', href: '/financials/comparison', icon: GitCompareArrows },
];

function FinancialsLayout() {
  const location = useLocation();

  // Hide tabs on cycle detail pages
  const isCycleDetail = location.pathname.includes('/financials/cycles/');

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Financials</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track costs, revenue, and profitability across your farm
        </p>
      </div>

      {/* Tab navigation */}
      {!isCycleDetail && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const isActive = tab.exact
                ? location.pathname === tab.href
                : location.pathname.startsWith(tab.href);

              return (
                <Link
                  key={tab.name}
                  to={tab.href as '/financials'}
                  className={cn(
                    'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.name}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Child routes */}
      <Outlet />
    </div>
  );
}
